import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Camera, CameraOff, ShieldCheck, XCircle, Hash } from "lucide-react";

import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const ENDPOINT_BY_KIND = {
  student: "/checkin/student",
  guest: "/checkin/guest",
  party: "/checkin/party",
};

const EXPECTED_QR_TYPE_BY_KIND = { student: "STUDENT", guest: "GUEST" };

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
}

export default function CheckinScanner({ kind }) {
  // kind: "student" | "guest" | "party"
  const endpoint = ENDPOINT_BY_KIND[kind];
  const [manual, setManual] = useState("");
  const [htno, setHtno] = useState("");
  const [pending, setPending] = useState(null); // lookup result awaiting confirm
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null); // final committed result or error
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const containerId = "qr-reader";
  const cooldown = useRef(0);
  const pausedRef = useRef(false);

  const pauseCamera = () => {
    const html5 = scannerRef.current;
    if (!html5 || pausedRef.current) return;
    try {
      const state = html5.getState ? html5.getState() : null;
      if (state === 2 /* SCANNING */) { html5.pause(true); pausedRef.current = true; }
    } catch (_e) { /* ignore */ }
  };

  const resumeCamera = () => {
    const html5 = scannerRef.current;
    if (!html5 || !pausedRef.current) return;
    try {
      html5.resume();
      pausedRef.current = false;
    } catch (_e) { /* ignore */ }
  };

  const lookup = async (qr_uuid) => {
    if (!qr_uuid) return;
    setResult(null);
    try {
      const r = await api.get("/checkin/lookup", { params: { qr_uuid: qr_uuid.trim() } });
      if (kind !== "party") {
        const expected = EXPECTED_QR_TYPE_BY_KIND[kind];
        if (expected && r.data.qr?.qr_type !== expected) {
          setResult({ error: `This is not a ${expected === "STUDENT" ? "student" : "guest"} QR.` });
          return;
        }
      }
      pauseCamera();
      setPending(r.data);
    } catch (e) {
      setResult({ error: apiErrorMessage(e) });
    }
  };

  const lookupByHtno = async (rollNo) => {
    if (!rollNo) return;
    setResult(null);
    try {
      const r = await api.get("/checkin/lookup/by-htno", { params: { htno: rollNo.trim() } });
      pauseCamera();
      setPending(r.data);
    } catch (e) {
      setResult({ error: apiErrorMessage(e) });
    }
  };

  const [guestPicker, setGuestPicker] = useState(null); // { student, guests } awaiting guest selection

  const lookupGuestsByHtno = async (rollNo) => {
    if (!rollNo) return;
    setResult(null);
    try {
      const r = await api.get("/checkin/lookup/guests-by-htno", { params: { htno: rollNo.trim() } });
      pauseCamera();
      setGuestPicker(r.data);
    } catch (e) {
      setResult({ error: apiErrorMessage(e) });
    }
  };

  const pickGuest = (qrUuid) => {
    setGuestPicker(null);
    lookup(qrUuid);
  };

  const cancelGuestPicker = () => {
    setGuestPicker(null);
    resumeCamera();
  };

  const submitHtno = () => {
    if (!htno.trim()) return;
    if (kind === "guest") lookupGuestsByHtno(htno);
    else lookupByHtno(htno);
    setHtno("");
  };

  const cancelPending = () => {
    setPending(null);
    resumeCamera();
  };

  const confirmCheckin = async () => {
    if (!pending) return;
    setConfirming(true);
    try {
      const r = await api.post(endpoint, { qr_uuid: pending.qr.qr_uuid });
      setResult(r.data);
      setPending(null);
      resumeCamera();
      if (r.data.status === "already_checked_in") {
        toast.warning("Already Checked In");
      } else {
        toast.success("Check-in successful");
      }
    } catch (e) {
      toast.error(apiErrorMessage(e));
      setPending({ ...pending, confirmError: apiErrorMessage(e) });
    } finally {
      setConfirming(false);
    }
  };

  const starting = useRef(false);

  const start = async () => {
    if (starting.current || scannerRef.current) return;
    starting.current = true;
    try {
      const html5 = new Html5Qrcode(containerId, false);
      scannerRef.current = html5;
      await html5.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: (vw, vh) => ({ width: Math.round(Math.min(vw, vh) * 0.7), height: Math.round(Math.min(vw, vh) * 0.7) }) },
        (decoded) => {
          const now = Date.now();
          if (pausedRef.current || now - cooldown.current < 1500) return;
          cooldown.current = now;
          lookup(decoded);
        },
        () => {}
      );
      setScanning(true);
    } catch (e) {
      toast.error("Camera unavailable: " + e.message);
      scannerRef.current = null;
    } finally {
      starting.current = false;
    }
  };

  const stop = async () => {
    const html5 = scannerRef.current;
    scannerRef.current = null;
    pausedRef.current = false;
    setScanning(false);
    if (!html5) return;
    try {
      const state = html5.getState ? html5.getState() : null;
      if (state === 2 /* SCANNING */ || state === 3 /* PAUSED */) {
        await html5.stop();
      }
      await html5.clear();
    } catch (_e) { /* ignore - element may already be gone on unmount */ }
  };

  useEffect(() => {
    return () => { stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const LABEL_BY_KIND = { student: "Student Check-in", guest: "Guest Check-in", party: "Night Party Check-in" };
  const label = LABEL_BY_KIND[kind];

  const partyBlocked = kind === "party" && pending && !pending.convocation_checked_in_at;
  const alreadyThisType = pending && (
    kind === "party" ? pending.party_checked_in_at : pending.convocation_checked_in_at
  );

  return (
    <div data-testid={`${kind}-checkin-page`}>
      <PageHeader
        title={label}
        subtitle="Scan or enter a QR, verify the details, then confirm the check-in."
      />

      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white border border-border rounded-xl p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="kicker">Camera Scanner</p>
            {scanning ? (
              <Button size="sm" variant="outline" onClick={stop} data-testid="stop-scan-btn">
                <CameraOff size={16} className="mr-1" /> Stop
              </Button>
            ) : (
              <Button size="sm" onClick={start} className="bg-primary hover:bg-primary/90" data-testid="start-scan-btn">
                <Camera size={16} className="mr-1" /> Start Scanning
              </Button>
            )}
          </div>
          <div className="relative w-full aspect-square max-w-md mx-auto bg-slate-900 rounded-xl overflow-hidden shadow-inner">
            {/* This div is owned exclusively by html5-qrcode - React must never render children into it,
                or React's reconciliation and the library's own DOM mutations fight over the same nodes.
                html5-qrcode draws its own square-cornered viewfinder border internally (not easily
                restyled without reaching into its DOM); these corner brackets are a purely decorative
                overlay on top, sized to roughly match the actual qrbox region underneath. */}
            <div id={containerId} className="w-full h-full [&_video]:object-cover [&_video]:w-full [&_video]:h-full" />
            {scanning && (
              <div className="absolute inset-[15%] pointer-events-none">
                <span className="absolute top-0 left-0 w-10 h-10 border-t-[4px] border-l-[4px] border-white/90 rounded-tl-2xl" />
                <span className="absolute top-0 right-0 w-10 h-10 border-t-[4px] border-r-[4px] border-white/90 rounded-tr-2xl" />
                <span className="absolute bottom-0 left-0 w-10 h-10 border-b-[4px] border-l-[4px] border-white/90 rounded-bl-2xl" />
                <span className="absolute bottom-0 right-0 w-10 h-10 border-b-[4px] border-r-[4px] border-white/90 rounded-br-2xl" />
              </div>
            )}
            {!scanning && (
              <p className="absolute inset-0 flex items-center justify-center text-white/50 text-sm pointer-events-none">
                Camera stopped
              </p>
            )}
          </div>

          <div className="mt-6 border-t pt-4">
            <p className="kicker mb-2">Manual QR Entry</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                data-testid="manual-qr-input"
                placeholder="Paste QR UUID"
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && manual.trim()) { lookup(manual); setManual(""); } }}
                className="h-11"
              />
              <Button
                data-testid="manual-checkin-btn"
                className="h-11 bg-primary hover:bg-primary/90 shrink-0"
                onClick={() => { lookup(manual); setManual(""); }}
              >
                Look Up
              </Button>
            </div>
          </div>

          <div className="mt-6 border-t pt-4">
            <p className="kicker mb-2 flex items-center gap-1.5"><Hash size={12} /> Check In by Roll Number</p>
            <p className="text-xs text-slate-500 mb-2">
              {kind === "guest"
                ? "Enter the STUDENT's roll number to pick which of their guests to check in — guests don't have their own roll number."
                : "For when the QR is lost or unreadable. Generates a QR for them if needed."}
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                data-testid="htno-checkin-input"
                placeholder="e.g. SE24MAEV005"
                value={htno}
                onChange={(e) => setHtno(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submitHtno(); }}
                className="h-11"
              />
              <Button
                data-testid="htno-checkin-btn"
                variant="outline"
                className="h-11 shrink-0"
                onClick={submitHtno}
              >
                Look Up
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-white border border-border rounded-xl p-4 sm:p-6" data-testid="checkin-result">
          {guestPicker ? (
            <div data-testid="guest-picker-panel" className="animate-in fade-in duration-200">
              <p className="kicker text-primary mb-4">Select Guest</p>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center font-serif text-xl font-semibold shrink-0">
                  {initials(guestPicker.student.student_name)}
                </div>
                <div className="min-w-0">
                  <h3 className="font-serif text-2xl leading-tight truncate">{guestPicker.student.student_name}</h3>
                  <p className="text-slate-500 mt-0.5">{guestPicker.student.htno}</p>
                </div>
              </div>
              <div className="space-y-2">
                {guestPicker.guests.map((g) => {
                  const alreadyIn = kind === "party" ? g.party_checked_in_at : g.convocation_checked_in_at;
                  return (
                    <button
                      key={g.qr.id}
                      type="button"
                      data-testid={`guest-picker-option-${g.qr.guest_index}`}
                      onClick={() => pickGuest(g.qr.qr_uuid)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-border hover:bg-slate-50 transition-colors text-left"
                    >
                      <span>
                        <b>Guest {g.qr.guest_index}: {g.qr.guest_name || "—"}</b>
                        <span className="block text-xs text-slate-500">{g.qr.guest_relation || "—"}</span>
                      </span>
                      {alreadyIn && <Badge variant="outline" className="shrink-0">Checked in</Badge>}
                    </button>
                  );
                })}
              </div>
              <Button
                data-testid="guest-picker-cancel-btn"
                variant="outline"
                className="w-full h-11 mt-4"
                onClick={cancelGuestPicker}
              >
                <XCircle size={16} className="mr-1" /> Cancel
              </Button>
            </div>
          ) : pending ? (
            <div data-testid="verify-panel" className="animate-in fade-in duration-200">
              <div className="flex items-center justify-between mb-4">
                <p className="kicker text-primary">Verify Before Check-in</p>
                <Badge variant="outline">{pending.qr?.qr_type}{pending.qr?.guest_index ? ` ${pending.qr.guest_index}` : ""}</Badge>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center font-serif text-xl font-semibold shrink-0">
                  {initials(pending.student.student_name)}
                </div>
                <div className="min-w-0">
                  <h3 className="font-serif text-2xl sm:text-3xl leading-tight truncate">{pending.student.student_name}</h3>
                  <p className="text-slate-500 mt-0.5">{pending.student.htno}</p>
                </div>
              </div>

              {pending.qr?.qr_type === "GUEST" && (
                <p className="text-sm text-slate-600 mt-3 bg-slate-50 rounded-md px-3 py-2">
                  Guest: <b>{pending.qr.guest_name || "—"}</b> ({pending.qr.guest_relation || "—"})
                </p>
              )}

              {pending.qr?.qr_type === "STUDENT" && pending.sash && (
                <div
                  className="inline-flex items-center gap-2 mt-3 px-3 py-2 rounded-md text-sm font-semibold text-white"
                  style={{ backgroundColor: pending.sash.color }}
                  data-testid="sash-badge"
                >
                  {pending.sash.label}
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 mt-6 text-sm">
                <div><p className="kicker">Entity</p><p>{pending.student.entity}</p></div>
                <div><p className="kicker">Program</p><p>{pending.student.program}</p></div>
                <div><p className="kicker">Specialization</p><p>{pending.student.specialization}</p></div>
              </div>

              {kind === "party" && (
                <div className={`mt-4 p-3 rounded-md text-sm font-medium ${pending.convocation_checked_in_at ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-primary"}`}>
                  {pending.convocation_checked_in_at
                    ? `Attended convocation at ${new Date(pending.convocation_checked_in_at).toLocaleString()}`
                    : "Has NOT checked in to convocation yet — party check-in is blocked."}
                </div>
              )}

              {alreadyThisType && (
                <div className="mt-3 p-3 rounded-md text-sm bg-amber-50 text-amber-700">
                  Already checked in ({kind === "party" ? "party" : "this"}) at {new Date(alreadyThisType).toLocaleString()}
                </div>
              )}

              {pending.confirmError && (
                <div className="mt-3 flex items-start gap-2 text-primary text-sm">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" /> <p>{pending.confirmError}</p>
                </div>
              )}

              <div className="flex gap-2 mt-6">
                <Button
                  data-testid="cancel-checkin-btn"
                  variant="outline"
                  className="flex-1 h-11"
                  onClick={cancelPending}
                >
                  <XCircle size={16} className="mr-1" /> Cancel
                </Button>
                <Button
                  data-testid="confirm-checkin-btn"
                  className="flex-1 h-11 bg-primary hover:bg-primary/90 disabled:opacity-40"
                  disabled={confirming || partyBlocked}
                  onClick={confirmCheckin}
                >
                  <ShieldCheck size={16} className="mr-1" /> Confirm Check-in
                </Button>
              </div>
            </div>
          ) : (
            <>
              <p className="kicker">Result</p>
              {!result && <p className="mt-6 text-slate-500 text-sm">Waiting for scan…</p>}

              {result?.error && (
                <div className="mt-6 flex items-start gap-3 text-primary">
                  <AlertTriangle /> <p>{result.error}</p>
                </div>
              )}

              {result?.student && (
                <div className="mt-4 animate-in fade-in duration-200">
                  <div className="flex items-center gap-2 mb-4">
                    {result.status === "checked_in" ? (
                      <Badge className="bg-emerald-600"><CheckCircle2 size={12} className="mr-1" /> Checked In</Badge>
                    ) : (
                      <Badge variant="secondary">Already Checked In</Badge>
                    )}
                    <Badge variant="outline">{result.qr?.qr_type}</Badge>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center font-serif text-xl font-semibold shrink-0">
                      {initials(result.student.student_name)}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-serif text-2xl sm:text-3xl leading-tight truncate">{result.student.student_name}</h3>
                      <p className="text-slate-500 mt-0.5">{result.student.htno}</p>
                    </div>
                  </div>
                  {result.qr?.qr_type === "STUDENT" && result.sash && (
                    <div
                      className="inline-flex items-center gap-2 mt-3 px-3 py-2 rounded-md text-sm font-semibold text-white"
                      style={{ backgroundColor: result.sash.color }}
                      data-testid="sash-badge-result"
                    >
                      {result.sash.label}
                    </div>
                  )}
                  <div className="grid grid-cols-1 gap-3 mt-6 text-sm">
                    <div><p className="kicker">Entity</p><p>{result.student.entity}</p></div>
                    <div><p className="kicker">Program</p><p>{result.student.program}</p></div>
                    <div><p className="kicker">Specialization</p><p>{result.student.specialization}</p></div>
                    <div><p className="kicker">Time</p><p>{new Date(result.checked_in_at).toLocaleString()}</p></div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
